import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { ApiResponse, PaginationInfo } from "../../types/api";
import { Project } from "../../types/models";
import { ProjectContextType } from "../../types/context";

export const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchProjects = useCallback(async (page: number = 1, perPage: number = 15): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get<ApiResponse<any>>(`/projects?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let projectsData: any[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          // Nested structure: response.data.data.data
          projectsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Direct array: response.data.data
          projectsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          projectsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        projectsData = responseData.data;
      }
      
      // Transform camelCase API response to snake_case to match Project model
      const transformedProjects = projectsData.map((project: any) => ({
        ...project,
        project_name: project.projectName || project.project_name,
        project_section: project.projectSection || project.project_section,
        project_category: project.projectCategory || project.project_category,
        reference_no: project.referenceNo || project.reference_no,
        location_area: project.locationArea || project.location_area,
        starting_date: project.startingDate || project.starting_date,
        completion_date: project.completionDate || project.completion_date,
        estimated_budget: project.estimatedBudget || project.estimated_budget,
        gst_percentage: project.gstPercentage || project.gst_percentage,
        gst_amount: project.gstAmount || project.gst_amount,
        total_amount: project.totalAmount || project.total_amount,
        project_description: project.projectDescription || project.project_description,
        quotation_no: project.quotationNo || project.quotation_no,
        lpo_no: project.lpoNo || project.lpo_no,
        lpo_value: project.lpoValue || project.lpo_value,
        lpo_status: project.lpoStatus || project.lpo_status,
        lpo_date: project.lpoDate || project.lpo_date,
        thumbnail_url: project.thumbnail || project.thumbnail_url,
        status: project.status || null, // Project status field
      }));
      
      setProjects(transformedProjects as Project[]);
      setPagination(paginationData);
    } catch (err: any) {
      const error = new Error(err.response?.data?.message || err.message || "Failed to fetch projects");
      setError(error);
      console.error("Failed to fetch projects:", err);
      setProjects([]); // Set empty array on error
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const addProject = useCallback(async (project: Partial<Project>): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post<ApiResponse<Project>>("/projects", project);
      setProjects((prev) => [...prev, response.data.data]);
    } catch (err: any) {
      const error = new Error(err.response?.data?.message || err.message || "Failed to add project");
      setError(error);
      console.error("Failed to add project:", err);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const editProject = useCallback(async (updatedProject: Partial<Project> & { id: number }): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put<ApiResponse<Project>>(`/projects/${updatedProject.id}`, updatedProject);
      setProjects((prev) =>
        prev.map((project) =>
          project.id === updatedProject.id ? response.data.data : project
        )
      );
    } catch (err: any) {
      const error = new Error(err.response?.data?.message || err.message || "Failed to edit project");
      setError(error);
      console.error("Failed to edit project:", err);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteProject = useCallback(async (id: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/projects/${id}`);
      setProjects((prev) => prev.filter((project) => project.id !== id));
    } catch (err: any) {
      const error = new Error(err.response?.data?.message || err.message || "Failed to delete project");
      setError(error);
      console.error("Failed to delete project:", err);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Lazy fetch: Only fetch projects when they're accessed and empty
  // This prevents unnecessary API calls when ProjectContext is used but projects aren't needed
  useEffect(() => {
    // Only auto-fetch if projects array is empty (first load)
    // Components that need projects will call fetchProjects explicitly with pagination
    if (projects.length === 0 && !loading) {
      fetchProjects(1, 15);
    }
  }, [fetchProjects, projects.length, loading]);

  const contextValue: ProjectContextType = {
    projects,
    pagination,
    loading,
    error,
    fetchProjects,
    addProject,
    editProject,
    deleteProject,
  } as any;

  return (
    <ProjectContext.Provider value={contextValue}>
      {children}
    </ProjectContext.Provider>
  );
};

